/**
 * Everything the dashboard asks the operator for. One origin (nginx proxies `/api` to the
 * backend), one session header, and errors that arrive already phrased for a person: the backend
 * answers `{error, error_description}`, and `ApiError.message` is that description — screens show
 * it verbatim rather than inventing their own wording.
 */

const BASE = '/api'

export class ApiError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string, message: string) {
    super(message)
    this.status = status
    this.code = code
  }

  /** The session is gone (expired, revoked, or signed out elsewhere). */
  get signedOut(): boolean {
    return this.status === 401
  }
}

let session: string | null = null
let onSignedOut: (() => void) | null = null

/** Sessions live in sessionStorage, not localStorage: closing the tab ends them, and nothing
 *  survives on a shared machine. The 12-hour server-side expiry still governs. */
const STORAGE_KEY = 'avritam.session'

export function loadSession(): string | null {
  if (session === null) {
    session = window.sessionStorage.getItem(STORAGE_KEY)
  }
  return session
}

export function setSession(token: string): void {
  session = token
  window.sessionStorage.setItem(STORAGE_KEY, token)
}

export function clearSession(): void {
  session = null
  window.sessionStorage.removeItem(STORAGE_KEY)
}

/**
 * A pending team invite. The token arrives in the invite link's URL fragment (a fragment never
 * reaches a server) and must survive the same-tab Google round-trip, so it is claimed into
 * sessionStorage at boot and redeemed once a session exists — whoever the person turns out to be,
 * organization or no organization.
 */
const INVITE_KEY = 'avritam.invite'

export function loadPendingInvite(): string | null {
  return window.sessionStorage.getItem(INVITE_KEY)
}

export function setPendingInvite(token: string): void {
  window.sessionStorage.setItem(INVITE_KEY, token)
}

export function clearPendingInvite(): void {
  window.sessionStorage.removeItem(INVITE_KEY)
}

/** Called when any request comes back 401, so the app can return to sign-in exactly once. */
export function whenSignedOut(handler: () => void): void {
  onSignedOut = handler
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {}
  const token = loadSession()
  if (token) headers.Authorization = `Bearer ${token}`
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const response = await fetch(BASE + path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (response.status === 204) return undefined as T

  const text = await response.text()
  if (!response.ok) {
    let code = 'error'
    let description = 'Something went wrong. Try again.'
    try {
      const parsed = JSON.parse(text) as { error?: string; error_description?: string }
      code = parsed.error ?? code
      description = parsed.error_description ?? description
    } catch {
      /* a non-JSON failure (a proxy, a gateway) keeps the generic sentence */
    }
    if (response.status === 401) {
      clearSession()
      onSignedOut?.()
    }
    throw new ApiError(response.status, code, description)
  }

  return (text ? JSON.parse(text) : undefined) as T
}

/* ---------- shapes the screens read ---------- */

export interface Organization {
  account_id: string
  display_name?: string
  permissions: string[]
}

export interface Me {
  user_id: string
  email: string
  display_name?: string
  picture_url?: string
  organizations: Organization[]
}

export interface Counts {
  total: number
  active: number
  shredded: number
}

export interface Overview {
  labels_published: Counts
  labels_received: Counts
  admin_devices: number
  reader_devices: number
  servers: number
  waiting_for_approval: number
  high_risk_devices: number
  setup: {
    admin_device_paired: boolean
    server_connected: boolean
    reader_device_invited: boolean
  }
}

export interface ReaderDevice {
  device_id: string
  name?: string
  assignment?: string
  status: 'ACTIVE' | 'WAITING_FOR_APPROVAL' | 'REVOKED'
  joined_at?: string
}

export interface AdminDevice {
  key_id: string
  name?: string
  status: 'ACTIVE' | 'REPLACED'
  paired_at?: string
}

export interface Devices {
  reader_devices: ReaderDevice[]
  admin_devices: AdminDevice[]
}

export interface Server {
  machine_id: string
  name?: string
  environment: 'sandbox' | 'prod'
  status: 'ACTIVE' | 'RETIRED'
  connected_by?: string
  connected_at?: string
}

export interface Member {
  user_id: string
  is_you: boolean
  email?: string
  display_name?: string
  picture_url?: string
  permissions: string[]
  joined_at?: string
}

export interface RiskFactor {
  rule: string
  points: number
  detail: string
}

export interface RiskScore {
  delegatee_device_id: string
  score: number
  computed_at: string
  factors: RiskFactor[]
}

export interface MintedQr {
  qr_payload: string
  expires_at: string
}

export interface RegistrationToken {
  registration_token: string
  expires_at: string
  env: string
}

export interface LabelDetail {
  public_ref: string
  label_id: string
  status: 'active' | 'expired' | 'erased'
  published_at?: string
  expires_at?: string
  published_by?: string
  reprint_url?: string
}

/* ---------- calls ---------- */

export const api = {
  me: () => request<Me>('GET', '/me'),

  createOrganization: (displayName: string) =>
    request<{ account_id: string; display_name: string; permissions: string[] }>(
      'POST', '/accounts', { display_name: displayName }),

  overview: (accountId: string) => request<Overview>('GET', `/accounts/${accountId}/overview`),
  devices: (accountId: string) => request<Devices>('GET', `/accounts/${accountId}/devices`),
  servers: (accountId: string) =>
    request<{ servers: Server[] }>('GET', `/accounts/${accountId}/servers`),
  team: (accountId: string) => request<{ members: Member[] }>('GET', `/accounts/${accountId}/team`),

  risk: () => request<RiskScore[]>('GET', '/risk/delegatees'),

  riskOf: (deviceId: string) =>
    request<RiskScore>('GET', `/risk/delegatees/${encodeURIComponent(deviceId)}`),

  riskSummary: (deviceId: string, refresh = false) =>
    request<{ delegatee_device_id: string; summary: string; computed_at: string }>(
      'GET', `/risk/delegatees/${encodeURIComponent(deviceId)}/summary${refresh ? '?refresh=1' : ''}`),

  pairingCode: (accountId: string) =>
    request<MintedQr & { pairing_token: string }>('POST', `/accounts/${accountId}/pairing-tokens`),

  readerInvite: (accountId: string, assignment: string | null) =>
    request<MintedQr & { invite_token: string }>(
      'POST', `/accounts/${accountId}/delegatee-invites`,
      assignment ? { assignment } : {}),

  registrationToken: (accountId: string, env: 'sandbox' | 'prod') =>
    request<RegistrationToken>('POST', `/accounts/${accountId}/registration-tokens`, { env }),

  labelByRef: (accountId: string, publicRef: string) =>
    request<LabelDetail>('GET',
      `/accounts/${accountId}/labels?public_ref=${encodeURIComponent(publicRef)}`),

  changePermissions: (accountId: string, userId: string, permissions: string[]) =>
    request<{ user_id: string; permissions: string[] }>(
      'PATCH', `/accounts/${accountId}/team/${userId}`, { permissions }),

  removeMember: (accountId: string, userId: string) =>
    request<void>('DELETE', `/accounts/${accountId}/team/${userId}`),

  inviteTeammate: (accountId: string, permissions: string[]) =>
    request<{ invite_token: string; expires_at: string; permissions: string[] }>(
      'POST', `/accounts/${accountId}/user-invites`, { permissions }),

  redeemTeamInvite: (inviteToken: string) =>
    request<{ account_id: string; account_name?: string; permissions: string[] }>(
      'POST', '/user-invites/redeem', { invite_token: inviteToken }),

  signOut: () => request<void>('POST', '/auth/sign-out'),

  /** The SDK settings file, downloaded rather than shown — it belongs on a classpath, not in a
   *  copy buffer. */
  sdkConfigUrl: (accountId: string) => `${BASE}/accounts/${accountId}/sdk/hosted-operator.properties`,
}

/** Downloads need the session header too, so they go through fetch and a temporary object URL
 *  rather than a bare link. */
export async function downloadSdkConfig(accountId: string): Promise<void> {
  const token = loadSession()
  const response = await fetch(api.sdkConfigUrl(accountId), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!response.ok) {
    throw new ApiError(response.status, 'download_failed',
      'That file could not be downloaded. Try again.')
  }
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = 'hosted-operator.properties'
  anchor.click()
  URL.revokeObjectURL(url)
}
